/**
 * ServicesApiClient
 *
 * GW-3055: Abstract base class for all gw-services-api endpoints.
 * Extends BaseApiClient with:
 *   - Base URL: services.{env}.generalwisdom.com/v1 (falls back to apiBaseUrl)
 *   - Accept-Version: 1.0 header on all requests
 *
 * GW-7195: The /v1 prefix is appended here so route paths like /personal/rfis
 * resolve to /v1/personal/rfis. Without it the request hits the API Gateway
 * root which returns 404 without CORS headers, causing browsers to report
 * "Failed to fetch".
 */

import { getConfig } from '@/config/runtimeConfig';

import { BaseApiClient, type RequestOptions } from './baseApiClient';

function servicesBaseUrl(): string {
  const url = getConfig().servicesApiBaseUrl ?? getConfig().apiBaseUrl;
  return url.endsWith('/v1') ? url : `${url}/v1`;
}

/**
 * GW-9566: Auth-related flags a subclass may opt into WITHOUT being able to
 * override the /v1 baseURLFactory (which must always win — see GW-7195). Only
 * the session/auth behavior is forwarded; the base URL is fixed here.
 */
type ServicesApiClientOptions = {
  requiresAuth?: boolean;
  suppressSessionExpired?: boolean;
};

export abstract class ServicesApiClient extends BaseApiClient {
  // GW-9566: Optional passthrough so a subclass (e.g. RFIMessagesAPI) can set
  // suppressSessionExpired without losing the /v1 baseURLFactory default. The
  // 4 other subclasses call super() with no args and keep identical behavior
  // (baseURLFactory=servicesBaseUrl, requiresAuth=true, suppressSessionExpired=false).
  constructor(options?: ServicesApiClientOptions) {
    // GW-6394: Use baseURLFactory to defer getConfig() until first API call.
    // GW-7195: Always ensure the base URL ends with /v1 so route paths like
    // /personal/rfis resolve to /v1/personal/rfis on the API Gateway. The
    // baseURLFactory is fixed here; only the auth flags come from `options`.
    super({
      baseURLFactory: servicesBaseUrl,
      ...(options?.requiresAuth !== undefined ? { requiresAuth: options.requiresAuth } : {}),
      ...(options?.suppressSessionExpired !== undefined
        ? { suppressSessionExpired: options.suppressSessionExpired }
        : {}),
    });
  }

  // GW-10374: Forward RequestOptions to super so per-request flags (e.g.
  // `skipAuth` for public marketplace reads) reach BaseApiClient.getHeaders.
  // The previous no-arg override silently dropped the options, so a request
  // could never opt out of the Authorization header on this client.
  protected override async getHeaders(options?: RequestOptions): Promise<Record<string, string>> {
    const headers = await super.getHeaders(options);
    return {
      ...headers,
      'Accept-Version': '1.0',
    };
  }
}
